import { API_BASE } from "./config";
import type { Definition, GrammarEntry } from "./search";
import { bounceIfUnauthorized } from "./session";

/**
 * Ask — a conversation scoped to a reading log, with the app's own functions as tools (日本-325).
 *
 * **Ask never owns a translation.** English reaches the reader by the agent writing the field on
 * the sentence, which arrives here as a `translation` frame so the row repaints without a reload.
 * A transcript is the wrong container for one: scroll ten sentences on and it is gone.
 *
 * **The thread is continuous per log and persists.** Opening a log again picks the conversation up
 * where it was; Clear is the only reset.
 */

export interface AskReference {
  sentenceId: string;
  text: string;
}

export interface AskToolCall {
  callId: string;
  name: string;
  arguments: Record<string, unknown>;
}

/**
 * One row of a thread.
 *
 * `payload` is the full tool result — for `dict`, the same `Definition[]` the dock renders, which
 * is what lets an answer be made of real `ResultCard`s rather than prose. `summary` is the one
 * muted monospace line, and the only part the model still sees once the result has aged out of its
 * context; both are kept, because the transcript draws the card either way.
 */
export interface AskMessage {
  id: string;
  role: "user" | "assistant" | "tool";
  createdAt: number;
  text: string;
  /** What the model thought before answering, when it emitted any. A collapsed disclosure above
   *  the answer — never inline, and never sent back to the model. */
  reasoning: string;
  references: AskReference[];
  toolCalls: AskToolCall[];
  callId: string;
  name: string;
  summary: string;
  payload: AskPayload | null;
  /** Whether a tool refused. Read back rather than assumed: the transcript is refetched the moment
   *  a turn finishes, so a row that drew as failed while it streamed would otherwise go black a
   *  second later and stay that way on every reload. */
  ok: boolean;
}

export interface AskPayload {
  query?: string;
  /**
   * The rows a tool result carries. **Two shapes, told apart by the TOOL NAME** (日本-519).
   *
   * `dict` and `present_definitions` carry `Definition`s; `grammar` and `present_grammar` carry
   * `GrammarEntry`s. The payload deliberately carries no discriminator of its own — the server
   * already distinguishes them by which tool was called, and a second spelling of that fact is a
   * second thing to keep in step. `askTranscript`'s `entriesOf` / `grammarEntriesOf` are the only
   * readers, and each asks the name first: a grammar entry has no `senses`, so handing one to
   * `ResultCard` throws rather than degrading.
   */
  entries?: Definition[] | GrammarEntry[];
  term?: string;
  state?: string;
  cardedAs?: string | null;
  sentenceId?: string;
  translation?: string;
}

export interface AskThread {
  logId: string;
  createdAt: number;
  clearedAt: number;
  messages: AskMessage[];
}

export interface AskLimits {
  historyBudgetTokens: number;
  toolMemoryTurns: number;
  maxRecallsPerTurn: number;
}

/**
 * What sending would cost.
 *
 * **Input is measured and output is guessed**, which is why they are separate fields rather than one
 * total: `outputSamples` says how many past answers the output figure rests on, and zero means it is
 * a fallback constant. `cost` is null when the catalogue quotes no price — *unknown*, not free.
 */
export interface AskEstimate {
  promptTokens: number;
  completionTokens: number;
  outputSamples: number;
  model: string;
  promptPerM: number | null;
  completionPerM: number | null;
  cost: number | null;
}

export interface AskConfig {
  /** Whether this deployment has a key at all. Gates the whole segment — the same question the
   * `訳 translate` affordance asks, answered from one payload so the two cannot disagree. */
  configured: boolean;
  model: string;
  limits: AskLimits;
}

/** What arrives on the stream. One `done` **or** one `error` is always last. */
export type AskFrame =
  | { type: "text"; text: string }
  | { type: "reasoning"; text: string }
  | { type: "toolCall"; callId: string; name: string; arguments: Record<string, unknown> }
  | {
      type: "toolResult";
      id: string;
      callId: string;
      name: string;
      summary: string;
      payload: AskPayload;
      ok: boolean;
    }
  | { type: "translation"; sentenceId: string; translation: string }
  | {
      type: "done";
      messageId: string;
      model: string;
      historyTokens: number;
      droppedTurns: number;
      promptTokens: number;
      completionTokens: number;
      cachedTokens: number;
    }
  | { type: "error"; message: string };

async function json<T>(path: string, init?: RequestInit, what = "reach Ask"): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new Error(detail ?? `Could not ${what} (${res.status})`);
  }
  return res.json();
}

/**
 * What the next request would cost, before it is sent.
 *
 * A POST that reads nothing, because the question and its chips are the input and neither fits in a
 * query string — `messages`' reason.
 */
export function askEstimate(
  logId: string,
  text: string,
  references: AskReference[],
  signal?: AbortSignal,
): Promise<AskEstimate> {
  return json<AskEstimate>(
    `/api/ask/logs/${logId}/estimate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, references }),
      signal,
    },
    "estimate the request",
  );
}

export function askConfig(): Promise<AskConfig> {
  return json<AskConfig>("/api/ask/config");
}

export function askThread(logId: string): Promise<AskThread> {
  return json<AskThread>(`/api/ask/logs/${logId}`);
}

export function saveAskLimits(patch: Partial<AskLimits>): Promise<AskConfig> {
  return json<AskConfig>(
    "/api/ask/settings",
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
    "save the Ask settings",
  );
}

/**
 * Take back the end of the thread — the AI's last reply, or your question if that is what is last.
 *
 * **Answers with the whole thread, and the caller must draw what comes back.** One reply is several
 * rows (the assistant turn that called a tool, the tool results, the prose), and they go together
 * because a tool result whose call is gone cannot be sent again. So there is no subtracting the row
 * that was clicked: the server decides the unit, and this is the thread that remains.
 */
export function deleteLastAskMessage(logId: string): Promise<AskThread> {
  return json<AskThread>(
    `/api/ask/logs/${logId}/messages/last`,
    { method: "DELETE" },
    "delete the last message",
  );
}

export async function clearAskThread(logId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/ask/logs/${logId}`, { method: "DELETE" });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    throw new Error(`Could not clear the conversation (${res.status})`);
  }
}

/**
 * Ask a question, calling `onFrame` as each line arrives.
 *
 * **NDJSON over a POST rather than `EventSource`.** A question carries reference chips, which do
 * not fit in a query string, and `EventSource` can only GET. The answer is read off the body as it
 * streams, which is the same thing an SSE reader does with one less protocol in between.
 *
 * A failure *after* the first byte arrives as an `error` frame rather than a rejected promise —
 * there is a 200 and a half-written answer on the wire by then, and throwing would discard both.
 * Only a request that never started rejects.
 */
export async function ask(
  logId: string,
  text: string,
  references: AskReference[],
  onFrame: (frame: AskFrame) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/ask/logs/${logId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, references }),
    signal,
  });
  if (!res.ok || !res.body) {
    bounceIfUnauthorized(res);
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new Error(detail ?? `Could not ask (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // A chunk boundary can land mid-line, so only whole lines are parsed and the tail is carried
    // forward. Splitting on every chunk without this drops the frame that straddles the boundary.
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) emit(line, onFrame);
  }
  emit(buffer, onFrame);
}

function emit(line: string, onFrame: (frame: AskFrame) => void) {
  const trimmed = line.trim();
  if (!trimmed) return;
  try {
    onFrame(JSON.parse(trimmed) as AskFrame);
  } catch {
    // One unreadable line costs one frame, not the answer already on screen.
  }
}
